import React, { useState, useRef, useEffect, useCallback } from 'react'
import './ChatInput.css'

/**
 * ChatInput Component
 *
 * Message input with send button, keyboard shortcuts, and @mention autocomplete
 *
 * @param {Object} props
 * @param {Function} props.onSend - Called when user sends a message
 * @param {boolean} props.disabled - Whether input is disabled (e.g., during thinking)
 * @param {string} props.placeholder - Input placeholder text
 * @param {Array} props.participants - Session participants for @mention autocomplete
 */
export const ChatInput = ({
  onSend,
  disabled = false,
  placeholder = 'Message MyAgent...',
  participants = [],
}) => {
  const [input, setInput] = useState('')
  const [mentionQuery, setMentionQuery] = useState(null) // null = not mentioning, string = query
  const [mentionIndex, setMentionIndex] = useState(0)
  const inputRef = useRef(null)
  const mentionRef = useRef(null)

  useEffect(() => {
    if (inputRef.current && !disabled) {
      inputRef.current.focus()
    }
  }, [disabled])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
      inputRef.current.style.height = inputRef.current.scrollHeight + 'px'
    }
  }, [input])

  const filteredParticipants = mentionQuery !== null
    ? participants.filter(p => p.displayName.toLowerCase().includes(mentionQuery.toLowerCase()))
    : []

  const insertMention = useCallback((participant) => {
    const textarea = inputRef.current
    if (!textarea) return
    const cursor = textarea.selectionStart
    // Find the @ position before cursor
    const before = input.substring(0, cursor)
    const atIndex = before.lastIndexOf('@')
    if (atIndex === -1) return
    const after = input.substring(cursor)
    const newInput = before.substring(0, atIndex) + `@${participant.displayName} ` + after
    setInput(newInput)
    setMentionQuery(null)
    setMentionIndex(0)
    // Set cursor after inserted mention
    requestAnimationFrame(() => {
      const newPos = atIndex + participant.displayName.length + 2
      textarea.setSelectionRange(newPos, newPos)
      textarea.focus()
    })
  }, [input])

  const handleChange = useCallback((e) => {
    const value = e.target.value
    setInput(value)

    // Detect @mention
    const cursor = e.target.selectionStart
    const before = value.substring(0, cursor)
    const atMatch = before.match(/(^|\s)@(\S*)$/)
    if (atMatch && participants.length > 0) {
      setMentionQuery(atMatch[2])
      setMentionIndex(0)
    } else {
      setMentionQuery(null)
    }
  }, [participants.length])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!input.trim() || disabled) return
    onSend(input.trim())
    setInput('')
    setMentionQuery(null)
    if (inputRef.current) inputRef.current.focus()
  }

  const handleKeyDown = (e) => {
    // @mention navigation
    if (mentionQuery !== null && filteredParticipants.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionIndex(i => (i + 1) % filteredParticipants.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionIndex(i => (i - 1 + filteredParticipants.length) % filteredParticipants.length)
        return
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault()
        insertMention(filteredParticipants[mentionIndex])
        return
      }
      if (e.key === 'Escape') {
        setMentionQuery(null)
        return
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  return (
    <form className="chat-input-form" onSubmit={handleSubmit}>
      <div className="chat-input-container">
        {mentionQuery !== null && filteredParticipants.length > 0 && (
          <div className="mention-popup" ref={mentionRef}>
            {filteredParticipants.map((p, i) => (
              <button
                key={p.roleId}
                type="button"
                className={`mention-item ${i === mentionIndex ? 'mention-item-active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); insertMention(p) }}
              >
                <span className="mention-item-name">{p.displayName}</span>
                <span className="mention-item-type">{p.roleType}</span>
              </button>
            ))}
          </div>
        )}
        <textarea
          ref={inputRef}
          className="chat-input"
          placeholder={placeholder}
          value={input}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={1}
        />
        <button
          type="submit"
          className="chat-send-button"
          disabled={!input.trim() || disabled}
          title="Send message (Enter)"
        >
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
          </svg>
        </button>
      </div>
    </form>
  )
}
